import { Flo } from '../../types/flo-auth-api'
import { EntitlementsResourceType } from '../../types/flo-enums'
import { getAuthToken, getUserData } from '../../utils/authentication'
import { createQueryParams } from '../../utils/floUtils'
import {
  floFetch,
  getCoreApiUrl,
  getEntitlementsUrl,
  getOffersUrl,
  getPaymentsUrl,
} from '../../utils/network'

export class FloAuth {
  protected BASE_URL = getCoreApiUrl()
  protected ENTITLEMENTS_URL = getEntitlementsUrl()
  protected OFFERS_URL = getOffersUrl()
  protected PAYMENTS_URL = getPaymentsUrl()

  public async login(
    reqParams: Flo.Auth.LoginRequest,
  ): Promise<Flo.Auth.LoginResponse | ErrorType> {
    try {
      const res = await floFetch(`${this.BASE_URL}/api/tokens`, {
        method: 'POST',
        body: JSON.stringify(reqParams),
      })

      if (res instanceof Response) return res.json()

      return res as ErrorType
    } catch (error) {
      console.error(error)
    }
  }

  public async refreshToken(
    reqParams: Flo.Auth.RefreshTokenRequest,
  ): Promise<Flo.Auth.LoginResponse> {
    try {
      const res = await floFetch(`${this.BASE_URL}/api/refresh-tokens`, {
        method: 'POST',
        body: JSON.stringify(reqParams),
      })
      if (res instanceof Response) return res.json()
    } catch (error) {
      console.error(error)
    }
  }

  public async getActivationCode(): Promise<Flo.Auth.ActivationCodeResponse | ErrorType> {
    try {
      const res = await floFetch(`${this.BASE_URL}/api/activation-codes/new`, {
        method: 'POST',
      })

      if (res instanceof Response) return res.json()

      return res
    } catch (error) {
      console.error(error)
    }
  }

  public async getActivationCodeStatus(
    reqParams: Flo.Auth.ActivationCodeRequest,
  ): Promise<Flo.Auth.LoginResponse | null> {
    try {
      const res = await floFetch(`${this.BASE_URL}/api/activation-codes/${reqParams.code}`, {
        method: 'GET',
      })
      if (res instanceof Response && res.status === 200) {
        return res.json()
      }

      return null
    } catch (error) {
      console.error(error)
    }
  }

  public async sendForgotPasswordRequest(
    reqParams: Flo.Auth.ForgotPasswordRequest,
  ): Promise<boolean> {
    try {
      const res = await floFetch(
        `${this.BASE_URL}/api/users/forgot-password?email=${reqParams.email}`,
        {
          method: 'POST',
        },
      )

      return res.status >= 200 && res.status < 300
    } catch (error) {
      console.error(error)
    }
  }

  public async checkEntitlements(
    reqParams: Flo.Entitlements.CheckEntitlementsRequest,
  ): Promise<boolean | ErrorType> {
    try {
      const res = await floFetch(
        `${this.ENTITLEMENTS_URL}/${reqParams.resourceType ?? EntitlementsResourceType.ALL}/${
          reqParams.resourceId
        }`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        },
      )

      if (res instanceof Response && res.status === 200) return true

      if ('error' in res && res.isNetworkError) {
        return res
      } else {
        return false
      }
    } catch (error) {
      console.error(error)
    }
  }

  public async createUser(reqParams: Flo.Auth.CreateUserRequest): Promise<boolean | ErrorType> {
    try {
      const res = await floFetch(`${this.BASE_URL}/api/users`, {
        method: 'POST',
        body: JSON.stringify(reqParams),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
      if (res.status !== 200 && res.status !== 201) {
        const err = res.body
        if (err?.errors?.children?.email) {
          return {
            error: 'signUpCreateAccountDuplicateEmailError',
            status: res.status,
            isNetworkError: false,
          }
        } else if (err?.message) {
          return {
            error: 'signUpCreateAccountError',
            status: res.status,
            isNetworkError: false,
          }
        }

        return false
      }

      return true
    } catch (error) {
      console.error(error)
    }

    return false
  }

  public async getOffers(reqParams: Flo.Offers.OffersRequest): Promise<Flo.Offers.OffersList> {
    if (!reqParams?.type) {
      reqParams.type = 'samsung'
    }

    try {
      const queryParams = createQueryParams(reqParams ?? {})
      const res = await floFetch(`${this.OFFERS_URL}/offers${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })

      if (res instanceof Response && res.status === 200) {
        const offers = (await res.json()) as Flo.Offers.OffersResponse
        return offers.data
      }
    } catch (error) {
      console.error(error)
    }
  }

  public async getOffer(
    reqParams: Flo.Offers.OfferingRequest,
  ): Promise<Flo.Offers.OfferingResponse> {
    try {
      const res = await floFetch(`${this.OFFERS_URL}/offerings/${reqParams?.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })

      if (res instanceof Response && res.status === 200) {
        const offer = (await res.json()) as Flo.Offers.OfferingResponse
        return offer
      }
    } catch (error) {
      console.error(error)
    }
  }

  public async createSubscription(
    reqParams: Flo.Payments.CreateSubscriptionRequest,
  ): Promise<Flo.Payments.SubscriptionResponse> {
    try {
      const res = await floFetch(`${this.PAYMENTS_URL}/subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(reqParams),
      })

      if (res instanceof Response) {
        const subscription = (await res.json()) as Flo.Payments.Subscription
        return { status: res.status >= 200 && res.status < 300, subscription }
      }
    } catch (error) {
      console.error(error)
    }

    return null
  }

  public async getUserSubscription(): Promise<Flo.Payments.Subscription> {
    try {
      const user = getUserData()
      const res = await floFetch(
        `${this.PAYMENTS_URL}/users/${user?.user_id}/subscriptions?onlyActive=true`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
          },
        },
      )

      if (res instanceof Response) {
        const subscriptions = (await res.json()) as Flo.Payments.Subscription[]
        if (subscriptions?.length) {
          return subscriptions.find((s) => s.status?.toLowerCase() === 'active')
        }
      }
    } catch (error) {
      console.error(error)
    }

    return null
  }
}
